/*
 * Created on 02.04.2016
 *
 * Copyright(c) 1995 - 2016 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */
import { AfterContentInit, AfterViewInit, Component, ContentChild, ContentChildren, EventEmitter, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChange, SimpleChanges, TemplateRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable, ReplaySubject, Subject, Subscription, merge, timer } from 'rxjs';
import { debounce, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { LanguageService } from '../i18n/language.service';
import { LoggingService } from '../utils/logging.service';
import { MultiSelectItem } from './filter/table-multi-select-filter.component';
import { Column } from './table-column';
import { TableCellContext , TableColumnDirective, TableFilterCellContext } from './table-column.directive';
import { Filter, TablePageSortAndFilter } from './table-page-sort-and-filter';
import { Page } from './table-page-sort.model';
import { TableDataRowDirective } from './table-row.directive';
import { I18N } from './table.component.i18n';

/**
 * Ermöglicht die Anzeige von sortierbaren, filterbaren und paginierbaren Tabellen.
 *
 * Kann Templates als &lt;ng-content&gt; entgegen nehmen. Siehe dafür:
 * <li> {@link TableComponent#isTemplateColumn}
 * <li> {@link TableComponent#showIcons}
 *
 * @author imam
 * @author stoc
 */

@Component({
    selector: 'common-tables-table, [common-tables-table]',
    templateUrl: 'table.component.html'
})
export class TableComponent implements AfterViewInit, AfterContentInit, OnChanges, OnDestroy {
    // this.page.content will be transformed to this.rows
    /**
     * Die Daten zum Anzeigen. Entweder das oder {@link #page} setzen.
     */
    @Input() rows: { [key: string]: any; }[] = [];
    /**
     * Die Daten zum Anzeigen. Entweder das oder {@link #rows} setzen.
     */
    @Input() page: Page;
    /**
     * Die Spaltendefinitionen.
     */
    @Input() columns: Column[] = [];
    /**
     * Die Überschrift über der Tabelle.
     */
    @Input() title: string;
    /**
     * Wenn true, dann werden über &lt;ng-content&gt; übergebene &lt;button&gt;'s oberhalb und unterhalb der
     * Komponente angezeigt.
     */
    @Input() showIcons: boolean = false;
    /**
     * Wenn true, dann wird die Pagesize innerhalb der Komponente angezeigt sowie auch die Möglichkeit diese
     * zu ändern.
     */
    @Input() showPageSize: boolean = true;
    /**
     * Wenn false, dann werden keine CSS-Hover-Effekte an Spalten gezeigt.
     */
    @Input() showHover: boolean = true;
    /**
     * Wenn true dann wird diese Komponente ein &lt;ng-template&gt; mit dem Namen "tableContent"
     * das als &lt;ng-content&gt; übergeben wird benutzt um die einzelnen Zellen zu rendern.
     * z.B. so:
     *
     * ```
     * <ng-template let-column="column" let-row="row" let-index="index" #tableContent>
     *    <ng-container [ngSwitch]="column.name">
     *        <ng-container *ngSwitchCase="'actions'"> ... </ng-container>
     *        <ng-container *ngSwitchDefault>{{row[column.name]}}</ng-container>
     *    </ng-container>
     * </ng-template>
     * ```
     *
     * ACHTUNG
     *
     * Diese Variante ist noch an den meisten Stellen in Benutzung. Aber eigentlich soll pro Spalte ein eigenes Template verwendet werden,
     * statt ein großes für alle mit Switch-Case. Siehe columnDirectives.
     * Außerdem tendieren wir dazu, viele überflüssige div hinzuzufügen, wie obige Beiespiel zeigt.
     * In der Regel ist hier ng-container besser.
     */
    @Input() isTemplateColumn: boolean = false;
    /**
     * Mit diesem Property können Filteroptionen für Spalten gesetzt werden, die im Tabellenkopf
     * angezeigt werden. Dafür muss zunächst an der Spaltenkonfiguration {@link Column#multiSelection} true sein.
     * Siehe {@link MultiselectionsItemsInput}.
     */
    @Input() multiselectionsItems: MultiselectionsItemsInput;
    /**
     * Wenn false, dann wird die Anzahl der angezeigten Zeilen nicht angezeigt.
     */
    @Input() showCount: boolean = true;
    /**
     * NICHT VERWENDET / UNUSED!
     */
    // TODO kann entfernt werden
    @Input() pageSize: number;
    /**
     * Wenn true, dann wird immer eine Scrollbar angezeigt.
     */
    @Input() forceScrollbar: boolean = false;
    /**
     * Entfernt die Paginierung oberhalb der Tabelle.
     */
    @Input() disableTopPagination: boolean = false;
    /**
     * Entfernt die Paginierung unterhalb der Tabelle.
     */
    @Input() disableBottomPagination: boolean = false;
    /**
     * Die Nachricht die angezeigt werden soll, wenn keine Daten vorhanden sind.
     */
    @Input() noDataMsg: string = '';
    /**
     * Wenn true, dann wird eine Checkbox-Spalte an 1. Stelle in der Tabelle gerendert.
     * Zusätzlich gibt es im Tabellenkopf eine Checkbox angezeigt, die alle Einträge
     * auswählt.
     */
    @Input() multiRowSelection: boolean;
    /**
     * Spaltentitel ausblenden. Z.B. für kleine Ansichten auf Dashboards.
     */
    @Input() disableColumnTitle: boolean = false;
    /**
     * Wird beim anklicken einer Spalte in der Tabelle emmited.
     */
    @Output() rowClick: EventEmitter<any> = new EventEmitter();

    /**
     * Die initiale Belegung für die Filter und Sortierung.
     */
    @Input() tablePageSortAndFilter: TablePageSortAndFilter;

    /**
     * Wird emittiert, wenn sich Paginerung, Sortierung oder Filterung ändern. Nutzende Komponenten
     * sollen daraufhin die Daten für die Page laden, z.B. mittels switchMap und einem HTTP-Service.
     *
     * @deprecated bitte tablePageSortAndFilterChange verwenden.
     * Das ist auch die Varianten, die der Angular-Namenskonvention für 2-way-databinding entspricht.
     */
    @Output() tablePageSortAndFilterChanged: EventEmitter<TablePageSortAndFilter> = new EventEmitter();

    /**
     * Wird emittiert, wenn sich Paginerung, Sortierung oder Filterung ändern. Nutzende Komponenten
     * sollen daraufhin die Daten für die Page laden, z.B. mittels switchMap und einem HTTP-Service.
     */
    @Output() tablePageSortAndFilterChange: EventEmitter<TablePageSortAndFilter> = new EventEmitter();

    /**
     * Gibt die selektierten Rows zurück, wenn {@link #multiRowSelection} true ist.
     */
    @Output() multiRowSelectionItems: EventEmitter<any> = new EventEmitter();

    /**
     * PRIVATE!
     *
     * Die Templates im Content der Komponente. Aus Abwärtskompatibilitätsgründen gilt Folgendes:
     * Gibt es nur ein Template, dann wird es zum Rendern der Zellen verwendet.
     *
     * Grundsätzlich ist die Variante zu bevorzugen, das Template für das Rendern der Zellen mit #tableContent
     * eindeutig zu kennzeichnen. Insbesondere dann, wenn noch Templates für weitere Zwecke verwendet werden.
     */
    @ContentChildren(TemplateRef, { descendants: false }) allTemplates: QueryList<TemplateRef<any>>;

    /**
     * PRIVATE!
     *
     * Das &lt;ng-template&gt; im Content der Komponente, das eine Zelle rendert.
     */
    @ContentChild('tableContent') tableContent: TemplateRef<any>;

    /**
     * PRIVATE!
     */
    columnTemplate: TemplateRef<any>;

    /**
     * PRIVATE!
     *
     * Filter- und Zell-Templates für einzelne Tabellenspalten. Das ist die "neuste Variante".
     * #tableContent kann zwar weiterhin verwendet werden, aber columnDirectives bieten den Vorteil,
     * das man gezielt für einzelne Spalten Tempaltes definieren kann und für die anderen die Default-Darstellung beibehalten kann.
     * Außerdem ist es die einzige Variante, um eigene Filterkomponenten für Spalten festzulegen.
     */
    @ContentChildren(TableColumnDirective, { descendants: true }) columnDirectives: QueryList<TableColumnDirective>;

    /**
     * Template um die Darstellung von Tabellenzeilen (mit Daten) anzupassen.
     */
    @ContentChild(TableDataRowDirective) dataRowDirective: TableDataRowDirective;

    /**
     * PRIVATE!
     *
     * Key ist der eineutige Spaltenmane (column.name). Value ist das Filter-Template, falls vorhanden.
     */
    columnFilterTemplateByName: { [name: string]: TemplateRef<TableFilterCellContext>; } = {};

    /**
     * PRIVATE!
     *
     * Key ist der eineutige Spaltenmane (column.name). Value ist das Cell-Template, falls vorhanden.
     */
    columnCellTemplateByName: { [name: string]: TemplateRef<TableCellContext>; } = {};

    /**
     * PRIVATE!
     *
     * Beinhaltet die Filter-Belegungen zu jeder filterbaren Spalte. Wird direkt an die Filter-Komponenten gebunden.
     * Der Key entspricht column.property.
     */
    filterControls: { [property: string]: UntypedFormControl; } = {};

    /**
     * PRIVATE!
     *
     * ob es mindestens eine Spalte gibt, für die die Sortierung aktiviert ist.
     */
    isSort: boolean = false;

    /**
     * PRIVATE!
     *
     * ob es mindestens eine Spalte gibt, für die die Filterung aktiviert ist.
     */
    isFilter: boolean = false;

    /**
     * PRIVATE!
     */
    i18n: any;

    /**
     * PRIVATE!
     *
     * Die wählbaren Items Items für die MultiSelektFilter. Der Key entspricht column.property.
     */
    multiSelectItemMap: Map<string, MultiSelectItem[]> = new Map<string, MultiSelectItem[]>();

    /**
     * PRIVATE!
     */
    multiRowSelectionSelectedRows = [];

    /**
     * PRIVATE!
     */
    multiRowSelectionAllSelected: boolean = false;

    private readonly languageSubscription: Subscription;

    /**
     * Neue Subjects welche ein TableInfo als Value aussenden können.
     */
    private tableChangedWithDebounceSubject: Subject<TablePageSortAndFilter> = new ReplaySubject<TablePageSortAndFilter>(1);
    private tableChangedWithoutDebounceSubject: Subject<TablePageSortAndFilter> = new ReplaySubject<TablePageSortAndFilter>(1);
    private mergedObservable2: Observable<TablePageSortAndFilter>;

    private ngUnsubscribe$: Subject<void> = new Subject<void>();

    /**
     * Das letzte über das Output emittierte TablePageSortAndFilter. Merken wir uns, um im onChanges festzustellen,
     * ob die Komponente den Change selbst verursacht hat.
     */
    private lastEmittedTablePageSortAndFilter: TablePageSortAndFilter;


    constructor(private languageService: LanguageService, private loggingService: LoggingService) {
        this.languageSubscription = languageService.registerForLanguageChanged(this, 'i18n', I18N);
    }

    ngOnInit() {
        // set default.
        if (!this.noDataMsg) {
            this.noDataMsg = this.i18n.noData;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.page) {
            // Seite wurde geladen -> prüfen ob leer.
            this.reloadPageWhenRowsAreEmpty();
        }

        if (this.page) {
            this.rows = this.page.content || [];
        }

        if (this.multiRowSelection) {
            if (!this.rows) {
                this.rows = [];
            }
            // iteriert durch alle Reihen einer Page und setzt das Property checked auf false
            this.rows.forEach(row => row.checked = false);
            this.multiRowSelectionAllSelected = false;
        }

        // Bei Setzen oder Ändern der Spaltenkonfiguration oder bei Setzen oder Änderungen von tablePageSortAndFilter,
        // die nicht von der Komponente selbst verursacht wurden.
        if (changes.columns ||
                changes.tablePageSortAndFilter && changes.tablePageSortAndFilter.currentValue !== this.lastEmittedTablePageSortAndFilter ) {
            const tpsf = changes.tablePageSortAndFilter;
            this.initSortAndFilter(tpsf && tpsf.currentValue, tpsf && tpsf.firstChange);
        }
    }

    ngAfterViewInit() {
        this.initTablePageSortAndFilterObservers();
    }

    ngAfterContentInit() {
        // Column-Templates by name cachen
        this.columnFilterTemplateByName = {};
        this.columnCellTemplateByName = {};
        this.columnDirectives.forEach(cd => {
            this.columnFilterTemplateByName[cd.name] = cd.filter && cd.filter.template;
            this.columnCellTemplateByName[cd.name] = cd.cell && cd.cell.template;
        });

        // Ältere Varianten für Templates. Nur, falls explizit aktiviert durch isTemplateColumn
        if(this.isTemplateColumn) {
            if (this.tableContent) {
                this.columnTemplate = this.tableContent;

            } else {
                if (this.allTemplates.length > 1) {
                    this.loggingService.warn(
                        'Mehr als 1 ng-template wurde in die Tabellenkomponente reingereicht, das kann zu Fehlern führen! '
                        + 'Dabei auch auf *ngIf o.ä. achten! Es wird das erste ng-template verwendet. Es soll #tableContent an dem ' +
                        'entsprechenden ng-template gesetzt werden um festzulegen, dass dieses ng-template genutzt werden soll!');
                }
                this.columnTemplate = this.allTemplates.first;
            }
        }
    }

    ngOnDestroy() {
        if (this.languageSubscription) {
            this.languageSubscription.unsubscribe();
        }
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onMultiRowSelectionSelectAll(checked: boolean) {
        // wenn alle selektiert sind, unselect all
        if (checked) {
            // füge alle in einem Kopie aller Reihen hinzu
            this.multiRowSelectionSelectedRows = this.rows.concat([]);
        } else {
            this.multiRowSelectionSelectedRows = [];
        }

        // de- oder selektiere alle
        this.rows.forEach(row => row.checked = checked);

        // gib alle zurück
        this.multiRowSelectionItems.emit(this.multiRowSelectionSelectedRows.concat([]));
    }

    onMultiRowSelectionSelectContent(row) {
        this.insertOrUpdateSelectedContent(row);
    }

    insertOrUpdateSelectedContent(item) {
        if (item) {
            // ist das selektierte Item bereits selektiert, dann deselektiere, andern falls analog. Löschen oder hinzufügen
            const index = this.multiRowSelectionSelectedRows.findIndex(
                c => JSON.stringify(c).toLowerCase() === JSON.stringify(item).toLowerCase());
            if (index > -1) {
                this.multiRowSelectionSelectedRows.splice(index, 1);
            } else {
                this.multiRowSelectionSelectedRows.push(item);
            }
        }

        this.multiRowSelectionAllSelected = this.multiRowSelectionSelectedRows.length === this.rows.length;
        // gib das neue Array zurück
        this.multiRowSelectionItems.emit(this.multiRowSelectionSelectedRows.concat([]));
    }


    onChangeSort(column: Column) {
        if (column.sort) {
            this.deactivateSortOnOtherColumns(column.name);
            this.onChangeSortOrFilter(false);
        }
    }

    onChangePage(page: Page) {
        this.emitTablePageSortAndFilter(page, false);
    }

    /**
     * Stößt das initiale Laden oder erneute Laden (Reload) der Page an.
     *
     * @param refresh ob das Laden auch dann forciert werden soll, wenn sich nichts an
     *  Paginierung, Sortierung oder Filterung geändert hat (Reload)
     */
    load(refresh?: boolean) {
        // Alles noch etwas unrund, da wir mehrere Quellen haben: Das optionale this.tablePageSortAndFilter zur Initialisierung
        // und die eigentlich Page, die aber erst nach dem ersten Laden vorhanden ist.
        // Um Reload zu ermöglichen, schauen wir immer zuerst nach der eigentlichen Page
        const pageFromTpsf = this.tablePageSortAndFilter &&
            { number: this.tablePageSortAndFilter.page, size: this.tablePageSortAndFilter.size };
        const page = this.page || pageFromTpsf || { number: 0, size: 10 };
        this.emitTablePageSortAndFilter(page, false, refresh);
        this.multiRowSelectionSelectedRows = []; // Selektion zurücksetzen!
    }

    onRowClick(row: any) {
        this.rowClick.emit(row);
    }

    getTotalElement() {
        if (this.page && this.page.totalElements) {
            return this.page.totalElements;
        } else {
            return this.rows.length;
        }
    }

    /**
     * Erzeugt und initialisiert die FilterControls und die Sortierung anhandder übegebenen tablePageSortAndFilter.
     *
     * @param tpsf die Änderungen der Werte für Filterung und Sorteierung oder null falls unverändert.
     * @param tpsfFirstChange true, falls es sich um die intiale Änderung von Tpsf handelt
     */
    private initSortAndFilter(tpsf: TablePageSortAndFilter, tpsfFirstChange: boolean) {

        this.mapConfigForMultiSelectFilter();

        // Ein FormControl für jede filterbare Spalte anlegen, für die noch kein FormControl existiert.
        // FormControl intialisieren oder aktualisieren mit dem Wert / den Werten aus dem initialen tablePageSortAndFilter
        this.columns
            .filter(column => column.property)
            .forEach(column => {
                let ctrl = this.filterControls[column.property];
                if (!ctrl) {
                    ctrl = new UntypedFormControl();
                    this.filterControls[column.property] = ctrl;
                    // Auf Änderungen des Filters reagieren
                    ctrl.valueChanges
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        // TODO: Debounce nicht bei allen Filtern
                        .subscribe(_ => this.onChangeSortOrFilter(true));
                }

                if (tpsf) {
                    // Filterwert intialisieren oder aktualisieren
                    const value = this.getInitialFilterValue(tpsf, column);
                    ctrl.setValue(value, { emitEvent: false });
                }
            });

        // ob mindestens ein Filter entfernt wurde, weil seine Spalte gelöscht wurde.
        let filterRemoved = false;

        // Filter-Controls, für die die Spalte nicht mehr existiert oder nicht mehr filterbar ist, löschen.
        Object.keys(this.filterControls)
            .filter(filterProperty => this.filterControls[filterProperty] && !this.columns.find(c => c.property === filterProperty))
            .forEach(filterProperty => {
                filterRemoved = true;
                delete this.filterControls[filterProperty];
            });

        // Filterzeile anzeigen, falls es mindestens eine Spalte gibt, für die die Filterung aktiviert ist.
        this.isFilter = this.columns.some(c => !!c.property);


        // Falls in tablePageSortAndFilter eine Sortierung gesetzt ist, übernehmen wir diese in die columns.
        // Das ist sicher irgendwie redundant und unschön.
        // Allerdings ist die Tabelle zur Zeit grundsätzlich so ausgelegt,
        // dass der Sortierstatus in den Columns geändert wird, obwohl diese ja nur als Input-Parameter reingehen.
        // Es müsste mal sauber die statische Konfiguration (z.B. Spaltennamen) von den dynamischen Anteilen
        // (wonach wird aktuell sortiert und gefiltert?) getrennt werden. Aktuell ist das aber nur schwer möglich, ohne
        // alles einzureißen. Ggf Kopie der Tabellenkomponente anlegen und diese dann erst an einer Stelle verwenden und dort
        // dann alles "sauber machen"...
        if (tpsf && tpsf.sortProperty) {
            const sortColumn = this.columns.find(c => c.name === tpsf.sortProperty);
            if (sortColumn) {
                // TODO Prüfen gegen dev
                sortColumn.sort = tpsf.sortDirection;
                this.deactivateSortOnOtherColumns(sortColumn.name);
            }
        }
        // Sortierzeile anzeigen, falls es mindestens eine Spalte gibt, für die die Sortierung aktiviert ist.
        this.isSort = this.columns.some(c => !!c.sort);


        if (tpsf && this.page) {
            // Paginierung ändern.
            // Das intiale Setzen bei noch nicht vorhandener Page passiert in load().
            // Etwas unrund... Das hängt sicher auch damit zusammen, dass unsere Paginerungskomponente
            // direkt an die Page bindet, die ja das Ergebnis der Anfrage der Datenquelle ist und eigentlich
            // immutable sein müsste.
            this.page.number = tpsf.page;
            this.page.size = tpsf.size;
        }


        if (tpsf && !tpsfFirstChange && this.page)  {
            // tablePageSortAndFilter wurde programmatisch geändert.
            // Page neu laden, jedoch nie bei der initialen Änderung, denn bei dieser steuert die nutzende Komponente mit load(), wann
            // das intiale Laden beginnen kann.
            // Die Page wird hier nicht auf 0 zurückgesetzt. Das muss der Code, der tablePageSortAndFilter ändert, bei Bedarf selbst tun.
            this.emitTablePageSortAndFilter(this.page, false);
        } else if (filterRemoved) {
            // Eine Spalte mit vorhandenem Filter wurde gelöscht
            // -> wie eine manuelle Filteränderung behandeln, also mit Rücksprung zur Seite 0.
            this.onChangeSortOrFilter(false);
        }

    }

    /**
     * Die Konfiguration der MultiSelectFilter erfolgt über die multiselectionsItems. Diese müssen in die für die MultiSelectKomponente
     * erforderliche MultiSelectItems gemappt werden. Die Konfiguration kann sowohl als Json-Object also auch über ein Array erfolgen.
     */
    private mapConfigForMultiSelectFilter() {
        if (this.multiselectionsItems) {
            Object.getOwnPropertyNames(this.multiselectionsItems).forEach((itemName) => {
                if (this.multiselectionsItems[itemName].constructor === Array) {
                    const multiSelectItems: MultiSelectItem[] = [];
                    (this.multiselectionsItems[itemName] as string[]).forEach(value => {
                        multiSelectItems.push({ name: value, displayName: value });
                    });
                    this.multiSelectItemMap[itemName] = multiSelectItems;
                } else {
                    const multiSelectItems: MultiSelectItem[] = [];
                    Object.getOwnPropertyNames(this.multiselectionsItems[itemName]).forEach(item => {
                        multiSelectItems.push({
                            name: item,
                            displayName: this.multiselectionsItems[itemName][item]
                        });
                    });
                    this.multiSelectItemMap[itemName] = multiSelectItems;
                }
            });
        }
    }

    private deactivateSortOnOtherColumns(columnName: string) {
        this.columns.forEach((element) => {
            if (element.sort && columnName !== element.name) {
                element.sort = 'inactive';
            }
        });
    }

    private onChangeSortOrFilter(debounce: boolean) {
        if (this.page) {
            // Auf erste Seite gehen nach Filter- oder Sortieränderungen.
            this.page.number = 0;

            this.emitTablePageSortAndFilter(this.page, debounce);
        }
    }

    /**
     * Initialisiert Observables welche einen übergebenen Callback aufrufen sobald sich am TablePageSortAndFilter etwas geändert hat, also
     * sobald paginiert, gefiltert oder sortiert wurde.
     */
    private initTablePageSortAndFilterObservers() {
        this.getTablePageSortAndFilterObservable()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(tpsf => {
                this.lastEmittedTablePageSortAndFilter = tpsf;
                this.tablePageSortAndFilterChanged.emit(tpsf);
                this.tablePageSortAndFilterChange.emit(tpsf);
            });

    }

    /** Erzeugt das Observable für Änderungen am  TablePageSortAndFilter, das also feuert sobald paginiert, gefiltert oder sortiert wurde.*/
    private getTablePageSortAndFilterObservable(): Observable<TablePageSortAndFilter> {
        const compareFunction =
            (params1: TablePageSortAndFilter, params2: TablePageSortAndFilter) => {
                const old_ = JSON.stringify({ ...params1, refresh: false });
                const new_ = JSON.stringify({ ...params2, refresh: false });
                return old_ === new_ && !params2.refresh;
            };
        const observable1 = this.tableChangedWithDebounceSubject
            .pipe(
                debounce(() => timer(300))
            );

        this.mergedObservable2 = merge(observable1, this.tableChangedWithoutDebounceSubject);
        return this.mergedObservable2.pipe(distinctUntilChanged(compareFunction));
    }

    /**
     * Sendet ein neues Value zu den Subscribern der Subjects aus, welches das auslösen einer neue Seiten-Anfrage zur Folge haben kann.
     */
    private emitTablePageSortAndFilter(page: { number: number, size: number }, debounce: boolean, refresh?: boolean) {

        const tablePageSortAndFilter = new TablePageSortAndFilter();
        tablePageSortAndFilter.refresh = refresh;
        tablePageSortAndFilter.size = page.size;
        tablePageSortAndFilter.page = page.number;
        tablePageSortAndFilter.filter = this.buildFilter();

        const sortColumn = this.findSortColumn();
        if (sortColumn) {
            // Hier column.name statt column.propery verwenden, da column.property nur für Filter gesetzt ist.
            tablePageSortAndFilter.sortProperty = sortColumn.name;
            tablePageSortAndFilter.sortDirection = sortColumn.sort as 'asc' | 'desc';
        }
        if (debounce) {
            this.tableChangedWithDebounceSubject.next(tablePageSortAndFilter);
        } else {
            this.tableChangedWithoutDebounceSubject.next(tablePageSortAndFilter);
        }
    }

    private findSortColumn(): Column /* Column */ {
        return this.columns.find(c => c.sort && c.sort !== 'inactive');
    }

    /**
     * Initialen Wert eines Filters ermitteln.
     * Also Konvertierung von TablePageSortAndFilter.filter in den Wert eines filterControls.
     */
    private getInitialFilterValue(tablePageSortAndFilter: TablePageSortAndFilter, column: Column) {
        let value: any | string[];
        if (tablePageSortAndFilter) {
            tablePageSortAndFilter.filter
                .filter(filter => column.property === filter.name)
                .forEach(filter => {
                    if (column.multiSelection) {
                        // Bei einer Mehrfachauswahl existiert für jeden gewählten Filter-Wert ein eigenes Filter-Objekt.
                        // Das ist nicht unbedingt intuitiv, wurde aber aus Gründen der Abwärtskompatibilität so beibehalten.
                        // Baut man das mal aus, sind hier sicher Vereinfachungen möglich.
                        value = (value || []).concat(filter.value);
                    } else {
                        value = filter.value;
                    }
                });
        }
        return value;
    }

    /**
     * Baut einen Filter-Array anhand der gesetzten Filter auf.
     * Also Konvertierung der Werte der filterControls in TablePageSortAndFilter.filter.
     */
    private buildFilter(): Filter[] {
        const filter: Filter[] = [];
        if (this.filterControls) {
            Object.getOwnPropertyNames(this.filterControls).forEach((filterProperty) => {
                const filterValue = this.filterControls[filterProperty].value;
                if (filterValue) {
                    if (filterValue.constructor === Array) {
                        // Bei einem Array (Mehrfachauswahl) wird für jedes Element ein eigenes Filter-Objekt erzeugt.
                        // Das ist nicht unbedingt intuitiv, wurde aber aus Gründen der Abwärtskompatibilität so beibehalten.
                        filterValue.forEach(item => {
                            filter.push({ name: filterProperty, value: item });
                        });
                    } else if (filterValue.constructor === Object) {
                        // Objekt kopieren. Z.B. bei Date-Range.
                        filter.push({ name: filterProperty, value: { ...filterValue } });
                    } else {
                        // Einzelwert
                        filter.push({
                            name: filterProperty,
                            value: filterValue
                        });
                    }
                }
            });
        }
        return filter;
    }

    /**
     * Wenn ich auf der letzten Seite bin und dort alle Inhalte lösche,
     * dann bleibe ich auf der Seite aber sie ist leer. Die Paginierungselemente verschwinden dann.
     * Daher gehe ich für diesen Fall automatisch eine Seite zurück (Spring Data macht das leider nicht automatisch...)
     */
    private reloadPageWhenRowsAreEmpty() {
        if (this.page && (this.page.content || []).length === 0 && this.page.number > 0) {
            this.page.number = this.page.number - 1;
            this.emitTablePageSortAndFilter(this.page, false);
        }
    }


}

/**
 * Die Datenstruktur ist ein Objekt, wobei der Key ${column.property} entspricht und der Wert:
 * <li> entweder ein String-Array (Model-value = Display-value),
 * <li> oder ein Objekt von Model-Value zu Display-Value ist.
 *
 * Wird nur genutzt als Eingabewert für die Tabellenkomponente.
 * NICHT verwechseln mit {@link MultiSelectItem}!
 */
export type MultiselectionsItemsInput = {
    // Name des Properties (z.B. userState)
    // Wert ist Entweder ein Object von ModelValue zu DisplayValue
    // oder ein String-Array (displayName=modelValue)
    [property: string]: { [modelValue: string]: string } | string[]
};
